import PropTypes from 'prop-types';
import React from 'react';

import styled, { css } from 'styled-components';

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  ${({ $disabled }) => $disabled && css`
    pointer-events: none;
  `}
`;

const Switch = styled.div`
  ${({
    theme,
    $active,
    $activeColor,
    $disabled
  }) => css`
    position: relative;
    display: inline-block;
    margin-right: 10px;
    width: 52px;
    height: 28px;
    padding: 0 7px;
    align-self: flex-start;
    flex-shrink: 0;
    border-radius: 100px;
    background-color: ${theme.colors.neutral.grey[2]};
    cursor: pointer;
    transition: background-color 0.2s ease;
    user-select: none;

    &:before {
      position: absolute;
      top: 4px;
      left: 4px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: ${theme.colors.neutral.white};
      box-shadow: 0 0.5px 2px 1px rgba(33, 48, 56, 0.15);
      content: '';
      transition: left 0.2s ease;
      z-index: 20;
    }

    ${$active && css`
      background-color: ${$activeColor || theme.colors.primary.purple};
      box-shadow: 0 0.5px 2px 1px rgba(33, 48, 56, 0.15);

      &:before {
        left: calc(100% - 24px);
      }
    `};

    ${$disabled && css`
      opacity: 0.5;
      pointer-events: none;
    `}
  `};
`;

const Label = styled.div`
  ${({
    theme,
    active
  }) => css`
    display: flex;
    height: 100%;
    min-width: 20px;
    align-items: center;
    justify-content: flex-end;

    color: #213038;
    font-size: 12px;

    user-select: none;
    z-index: 10;

    ${active && css`
      color: #fff;
      justify-content: flex-start;
    `};
  `};
`;

const Input = styled.input`
  display: none;
`;

const Description = styled.span`
  display: inline-block;
  user-select: none;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: 1 0 auto;
  }
`;

/**
 * @tags styled, switch, toggle, form field
 */
function OnOffSwitch({
  disabled, active, activeColor, label, children, name, qa, ...props
}) {
  return (
    <Wrapper data-qa={qa} $disabled={disabled} {...props}>
      <Switch $disabled={disabled} $active={active} $activeColor={activeColor}>
        <Input
          type="checkbox"
          name={name}
          checked={active}
          readOnly
        />
        <Label active={active}>{label}</Label>
      </Switch>

      {children &&
        <Description className="dc-on-off-switch__description">{children}</Description>}
    </Wrapper>
  );
}

OnOffSwitch.displayName = 'OnOffSwitch';

OnOffSwitch.propTypes = {
  active: PropTypes.bool,
  activeColor: PropTypes.string,
  children: PropTypes.node,
  label: PropTypes.string,
  name: PropTypes.string,
  qa: PropTypes.string,
  disabled: PropTypes.bool
};

OnOffSwitch.defaultProps = {
  active: false,
  activeColor: '',
  name: 'switch',
  label: 'Off',
  children: undefined,
  qa: undefined,
  disabled: false
};

/** @component */
export default OnOffSwitch;
